<template>
  <div class="detail-view">
    <div class="detail-view-intro">
      <h1 class="detail-view-title">
        Detalhamento das pendências · {{ agentName }}
      </h1>
    </div>

    <p v-if="loading" class="detail-view-loading">
      Carregando dados das pendências…
    </p>

    <template v-else>
      <div class="detail-stats-label">Reservas</div>
      <div class="detail-stats detail-stats--3col">
        <StatTile
          label="Com pendência"
          :value="totals.detectadas"
          icon="mdi-clipboard-alert-outline"
          color="warning"
        />
        <StatTile
          label="Resolvidas pelo agente"
          :value="totals.resolvidas"
          icon="mdi-check-decagram-outline"
          color="success"
          :badge="`${totals.resolvidasRate}%`"
        />
        <StatTile
          label="Resolvidas pelo agente via reimportação"
          :value="totals.resolvidasReimport"
          icon="mdi-file-sync-outline"
          color="info"
        />
        <StatTile
          label="Na fila do agente"
          :value="totals.filaAgenteTotal || 0"
          icon="mdi-robot-outline"
          color="secondary"
        />
        <StatTile
          label="Correção manual"
          :value="totals.manualTotal || 0"
          icon="mdi-account-alert-outline"
          color="error"
        />
      </div>

      <div class="detail-stats-label">Pendências</div>
      <div class="detail-stats detail-stats--4col">
        <StatTile
          label="Total"
          :value="totals.totalPendencias"
          icon="mdi-format-list-numbered"
          color="warning"
        />
        <StatTile
          label="Resolvidas pelo agente"
          :value="totals.pendenciasResolvidas"
          icon="mdi-check-decagram-outline"
          color="success"
          :badge="`${totals.pendenciasRate}%`"
        />
        <StatTile
          label="Restantes na fila do agente"
          :value="totals.pendenciasRestantesAgente"
          icon="mdi-robot-outline"
          color="secondary"
        />
        <StatTile
          label="Restantes para correção manual"
          :value="totals.pendenciasRestantesManual"
          icon="mdi-account-alert-outline"
          color="error"
        />
      </div>

      <div class="detail-stats-label">
        Saíram da pendência (ação da equipe no Phoenix)
      </div>
      <div class="detail-stats detail-stats--3col">
        <StatTile
          label="Em rascunho agora"
          :value="totals.rascunho || 0"
          icon="mdi-file-edit-outline"
          color="warning"
        />
        <StatTile
          label="Finalizadas pela equipe"
          :value="totals.finalizadasEquipe || 0"
          icon="mdi-account-check-outline"
          color="success"
        />
        <StatTile
          label="Canceladas no Phoenix"
          :value="totals.canceladasPhoenix || 0"
          icon="mdi-cancel"
          color="secondary"
        />
      </div>

      <section class="detail-section">
        <div class="detail-section-title">
          Situação das pendências detectadas
        </div>
        <div v-if="totalBarra" class="progress">
          <div class="progress-bar">
            <div
              v-for="seg in segments"
              :key="seg.key"
              class="progress-seg"
              :class="`progress-seg--${seg.key}`"
              :style="{ width: pct(seg.value) }"
              :title="`${seg.label}: ${seg.value}`"
            >
              <span v-if="segLabelVisible(seg.value)">{{ seg.value }}</span>
            </div>
          </div>
          <div class="progress-legend">
            <span
              v-for="seg in segments"
              :key="seg.key"
              class="progress-legend-item"
            >
              <span class="dot" :class="`dot--${seg.key}`"></span>
              {{ seg.value }} {{ seg.label }}
            </span>
          </div>
        </div>
        <p v-else class="detail-empty">
          Nenhuma pendência detectada neste período.
        </p>
      </section>

      <section
        v-for="group in groups"
        :key="group.key"
        class="detail-section pendency-group"
      >
        <div
          class="pendency-group-head pendency-group-head--clicavel"
          :class="`pendency-group-head--${group.key}`"
          role="button"
          tabindex="0"
          :aria-expanded="grupoAberto(group.key) ? 'true' : 'false'"
          @click="toggleGrupo(group.key)"
          @keydown.enter.prevent="toggleGrupo(group.key)"
          @keydown.space.prevent="toggleGrupo(group.key)"
        >
          <v-icon
            size="18"
            class="pendency-group-chevron"
            :class="{ 'is-open': grupoAberto(group.key) }"
            >mdi-chevron-right</v-icon
          >
          <div class="pendency-group-head-icon">
            <v-icon size="18" :color="group.iconColor">{{ group.icon }}</v-icon>
          </div>
          <div class="pendency-group-head-text">
            <span class="pendency-group-title">{{ group.title }}</span>
            <span class="pendency-group-sub">{{ group.subtitle }}</span>
          </div>
          <span class="pendency-group-count">{{ contagemGrupo(group) }}</span>
        </div>

        <div
          v-if="group.items.length"
          v-show="grupoAberto(group.key)"
          class="detail-table-wrap"
        >
          <table class="detail-table pendency-table">
            <colgroup>
              <col class="col-chevron" />
              <col class="col-reserva" />
              <col class="col-cliente" />
              <col class="col-chegada" />
              <col class="col-status" />
              <col class="col-email" />
              <col class="col-detectada" />
              <col class="col-resolvida" />
            </colgroup>
            <thead>
              <tr>
                <th></th>
                <th>Reserva</th>
                <th>Cliente</th>
                <th>Chegada</th>
                <th>Situação</th>
                <th>E-mail à CVC</th>
                <th>Detectada</th>
                <th>Resolvida</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="item in group.items">
                <tr
                  :key="item.id"
                  class="pendency-row"
                  :class="{
                    'is-open': openId === item.id,
                    'pendency-row--manual': item.tratamento === 'manual',
                  }"
                  @click="toggle(item.id)"
                >
                  <td class="chevron-cell">
                    <v-icon
                      size="18"
                      class="chevron"
                      :class="{ 'is-open': openId === item.id }"
                    >
                      mdi-chevron-right
                    </v-icon>
                  </td>
                  <td>
                    <div class="pendency-codes">
                      <span class="pendency-voucher">{{
                        item.voucher || item.recCode || "—"
                      }}</span>
                      <span
                        v-if="item.voucher && item.recCode"
                        class="pendency-rec"
                        >{{ item.recCode }}</span
                      >
                    </div>
                  </td>
                  <td class="ellipsis">{{ item.cliente || "—" }}</td>
                  <td>{{ item.dataChegada || "—" }}</td>
                  <td>
                    <span class="tag" :class="`tag--${item.tratamento}`">{{
                      tratamentoLabel(item.tratamento)
                    }}</span>
                  </td>
                  <td>
                    <v-tooltip bottom max-width="380">
                      <template v-slot:activator="{ on, attrs }">
                        <span
                          class="tag tag--email"
                          :class="`tag--email-${item.emailCvc.status}`"
                          v-bind="attrs"
                          v-on="on"
                          >{{ emailLabel(item.emailCvc.status) }}</span
                        >
                      </template>
                      <div
                        v-for="(linha, i) in emailTooltip(item.emailCvc)"
                        :key="i"
                      >
                        {{ linha }}
                      </div>
                    </v-tooltip>
                  </td>
                  <td class="muted">{{ item.detectadaEm }}</td>
                  <td class="muted">
                    {{
                      item.status === "resolvida" ||
                      item.status === "resolvida_reimport"
                        ? item.resolvidaEm || item.execResolvidaDate || "—"
                        : item.saiu
                        ? item.saiuEm || "—"
                        : "—"
                    }}
                  </td>
                </tr>
                <tr
                  v-if="openId === item.id"
                  :key="item.id + '-detail'"
                  class="pendency-detail-row"
                >
                  <td colspan="8">
                    <div class="pendency-detail">
                      <div class="pendency-detail-block">
                        <span class="pendency-detail-label">E-mail à CVC</span>
                        <p class="pendency-detail-motivo">
                          <template v-if="item.emailCvc.status === 'enviado'"
                            >Enviado em {{ item.emailCvc.quando }}.</template
                          >
                          <template
                            v-else-if="item.emailCvc.status === 'nao_enviado'"
                            >Não enviado — {{ item.emailCvc.motivo }}.</template
                          >
                          <template
                            v-else-if="item.emailCvc.status === 'falhou'"
                            >Falha ao enviar:
                            {{ item.emailCvc.motivo }}.</template
                          >
                          <template v-else
                            >Sem registro de e-mail para este voucher.</template
                          >
                        </p>
                        <span class="pendency-detail-label"
                          >Pendência original{{
                            item.pendencyCount ? ` · ${item.pendencyCount}` : ""
                          }}</span
                        >
                        <div
                          v-if="item.pendencyTypes.length"
                          class="pendency-detail-missing"
                        >
                          <span
                            v-for="t in item.pendencyTypes"
                            :key="t"
                            class="chip chip--tipo"
                            >{{ t }}</span
                          >
                        </div>
                        <ul
                          v-if="item.motivoLinhas.length"
                          class="pendency-detail-motivo-list"
                        >
                          <li v-for="(m, mi) in item.motivoLinhas" :key="mi">
                            {{ m }}
                          </li>
                        </ul>
                        <p v-else class="pendency-detail-motivo">—</p>
                        <template v-if="item.ultimaTentativa">
                          <span class="pendency-detail-label"
                            >Resultado da última tentativa</span
                          >
                          <p class="pendency-detail-motivo">
                            {{ item.ultimaTentativa }}
                          </p>
                        </template>
                        <p
                          v-if="item.motivoManual"
                          class="pendency-detail-manualnote"
                        >
                          <v-icon size="13" color="#b06a12"
                            >mdi-account-alert-outline</v-icon
                          >
                          {{ item.motivoManual }}
                        </p>
                        <div
                          v-if="item.missingFields.length"
                          class="pendency-detail-missing"
                        >
                          <span class="pendency-detail-label"
                            >Campos que faltam</span
                          >
                          <span
                            v-for="f in item.missingFields"
                            :key="f"
                            class="chip"
                            >{{ f }}</span
                          >
                        </div>
                      </div>

                      <div v-if="item.apelidoHotel" class="pendency-detail-block">
                        <span class="pendency-detail-label">
                          Apelido de hotel · avaliação do agente
                          <span
                            v-if="item.apelidoHotel.resultado === 'simulada'"
                            class="chip"
                            >Simulação</span
                          >
                        </span>
                        <p class="pendency-detail-motivo">
                          <strong>{{ item.apelidoHotel.apelido }}</strong>
                          <template v-if="item.apelidoHotel.hotel">
                            →
                            {{ item.apelidoHotel.hotel.name }}
                            <span class="muted">
                              (id {{ item.apelidoHotel.hotel.id }})</span
                            >
                          </template>
                        </p>
                        <p class="pendency-detail-motivo">
                          <template
                            v-if="
                              item.apelidoHotel.resultado === 'criado' ||
                              item.apelidoHotel.resultado === 'ja_existia'
                            "
                            >{{
                              item.apelidoHotel.resultado === "criado"
                                ? "Apelido cadastrado pelo agente"
                                : "O apelido já existia"
                            }}
                            · certeza {{ item.apelidoHotel.confianca }}%<template
                              v-if="
                                item.apelidoHotel.nivel &&
                                item.apelidoHotel.nivel !== 'existente'
                              "
                            >
                              ({{ item.apelidoHotel.nivel }})</template
                            >.</template
                          >
                          <template v-else
                            >{{
                              item.apelidoHotel.resultado === "simulada"
                                ? "Simulação: nada foi gravado."
                                : "O agente não teve certeza e nada foi gravado."
                            }}
                            <template v-if="item.apelidoHotel.confianca != null"
                              >Melhor candidato com
                              {{ item.apelidoHotel.confianca }}%.</template
                            >
                            <template v-if="item.apelidoHotel.motivo">
                              {{ item.apelidoHotel.motivo }}</template
                            ></template
                          >
                        </p>
                        <ul
                          v-if="item.apelidoHotel.candidatos.length"
                          class="pendency-detail-motivo-list"
                        >
                          <li
                            v-for="(c, ci) in item.apelidoHotel.candidatos"
                            :key="ci"
                          >
                            {{ c.name }} — {{ c.confianca }}%
                          </li>
                        </ul>
                        <p
                          v-if="
                            item.apelidoHotel.sugestao &&
                            item.apelidoHotel.resultado !== 'criado' &&
                            item.apelidoHotel.resultado !== 'ja_existia'
                          "
                          class="pendency-detail-manualnote"
                        >
                          {{ item.apelidoHotel.sugestao }}
                        </p>
                      </div>

                      <div class="pendency-detail-block">
                        <span class="pendency-detail-label">
                          Tentativas de resolução
                          <template v-if="item.tentativas.length"
                            >· {{ item.tentativas.length }}</template
                          >
                        </span>
                        <div
                          v-if="item.tentativas.length"
                          class="attempt-groups"
                        >
                          <div
                            v-for="grupo in item.tentativas"
                            :key="grupo.execId"
                            class="attempt-group"
                          >
                            <div class="attempt-group-head">
                              <span
                                class="attempt-badge"
                                :class="`attempt-badge--${grupo.resultado}`"
                                >{{ grupo.resultadoLabel }}</span
                              >
                              <span class="attempt-group-meta"
                                >execução #{{ grupo.execId }}
                                <template v-if="grupo.execDate"
                                  >· {{ grupo.execDate }}</template
                                ></span
                              >
                            </div>
                            <ul class="attempt-list">
                              <li
                                v-for="(t, i) in grupo.itens"
                                :key="i"
                                class="attempt"
                              >
                                <span
                                  class="attempt-dot"
                                  :class="{
                                    'attempt-dot--err': t.status === 'error',
                                  }"
                                ></span>
                                <span class="attempt-time">{{ t.time }}</span>
                                <span class="attempt-text">{{ t.text }}</span>
                              </li>
                            </ul>
                          </div>
                        </div>
                        <p v-else class="pendency-detail-none">
                          Nenhuma tentativa do Resolver ainda — segue aguardando
                          resposta do parceiro.
                        </p>
                      </div>

                      <div
                        class="pendency-detail-block pendency-detail-block--meta"
                      >
                        <div>
                          <span class="pendency-detail-label"
                            >Detectada na execução</span
                          >
                          <span class="pendency-detail-meta">
                            {{
                              item.execCriadaId ? `#${item.execCriadaId}` : "—"
                            }}
                            <template v-if="item.execCriadaDate"
                              >· {{ item.execCriadaDate }}</template
                            >
                          </span>
                        </div>
                        <div>
                          <span class="pendency-detail-label"
                            >Última execução do Resolver</span
                          >
                          <span class="pendency-detail-meta">
                            {{
                              item.execResolvidaId
                                ? `#${item.execResolvidaId}`
                                : "—"
                            }}
                            <template v-if="item.execResolvidaDate"
                              >· {{ item.execResolvidaDate }}</template
                            >
                          </span>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
        <p v-else v-show="grupoAberto(group.key)" class="detail-empty">
          {{ group.empty }}
        </p>
      </section>

      <ListaRecolhivel titulo="Reservas que saíram da pendência e estão em rascunho" :total="saidas.meta.rascunhos">
        <SaidasLista
          tipo="rascunho"
          :items="saidas.rascunhos"
          :meta="{
            total: saidas.meta.rascunhos,
            listadas: saidas.rascunhos.length,
          }"
        />
      </ListaRecolhivel>

      <ListaRecolhivel titulo="Reservas finalizadas pela equipe no Phoenix" :total="saidas.meta.finalizadas">
        <SaidasLista
          tipo="finalizada"
          :items="saidas.finalizadas"
          :meta="{
            total: saidas.meta.finalizadas,
            listadas: saidas.finalizadas.length,
          }"
        />
      </ListaRecolhivel>

      <ListaRecolhivel titulo="Reservas canceladas no Phoenix" :total="saidas.meta.canceladas">
        <SaidasLista
          tipo="cancelada"
          :items="saidas.canceladas"
          :meta="{
            total: saidas.meta.canceladas,
            listadas: saidas.canceladas.length,
          }"
        />
      </ListaRecolhivel>
    </template>
  </div>
</template>

<script>
import { getPendenciesDetail } from "@/services/nexiaApi.js";
import StatTile from "@/components/StatTile/StatTile.vue";
import SaidasLista from "./SaidasLista.vue";
import ListaRecolhivel from "@/components/ListaRecolhivel/ListaRecolhivel.vue";

const TRATAMENTO_LABEL = {
  agente: "Fila do agente",
  manual: "Correção manual",
  resolvida: "Resolvida",
  resolvida_reimport: "Resolvida (CVC no portal)",
};

const EMPTY_SAIDAS = {
  rascunhos: [],
  finalizadas: [],
  canceladas: [],
  meta: { rascunhos: 0, finalizadas: 0, canceladas: 0 },
};

const EMPTY_TOTALS = {
  detectadas: 0,
  resolvidas: 0,
  resolvidasReimport: 0,
  resolvidasTotal: 0,
  pendenteAgente: 0,
  pendenteManual: 0,
  emPendencia: 0,
  reservasRestantes: 0,
  resolvidasRate: 0,
  totalPendencias: 0,
  pendenciasResolvidas: 0,
  pendenciasRestantes: 0,
  pendenciasRestantesAgente: 0,
  pendenciasRestantesManual: 0,
  pendenciasRate: 0,
  equipeFila: 0,
  equipeManual: 0,
  equipeTotal: 0,
  filaAgenteTotal: 0,
  manualTotal: 0,
  pendenciasEquipe: 0,
  pendenciasEquipeFila: 0,
  pendenciasEquipeManual: 0,
};

export default {
  name: "PendenciesDetail",
  components: { StatTile, SaidasLista, ListaRecolhivel },
  props: {
    range: { type: Object, required: true },
    agent: { type: String, default: null },
    agentName: { type: String, default: "" },
    base: { type: String, default: null },
  },
  data() {
    return {
      loading: false,
      totals: EMPTY_TOTALS,
      items: [],
      saidas: EMPTY_SAIDAS,
      openId: null,
      // grupos abertos (comecam todos recolhidos); a chave e o "key" do grupo
      gruposAbertos: {},
    };
  },
  computed: {
    // A barra e sobre PENDENCIAS (nao reservas): resolvidas x restantes.
    segments() {
      return [
        {
          key: "resolvida",
          label: "pendências resolvidas",
          value: this.totals.pendenciasResolvidas,
        },
        {
          key: "restante_agente",
          label: "restantes na fila do agente",
          value: this.totals.pendenciasRestantesAgente,
        },
        {
          key: "restante_manual",
          label: "restantes para correção manual",
          value: this.totals.pendenciasRestantesManual,
        },
      ].filter((s) => s.value > 0);
    },
    // A barra e as larguras usam so o que ela mostra (resolvidas + restantes), para sempre fechar em 100%.
    totalBarra() {
      return this.segments.reduce((s, seg) => s + seg.value, 0);
    },
    groups() {
      // A lista mostra TODAS as reservas do grupo (as mesmas que o cartao conta): as abertas primeiro.
      const by = (t) =>
        this.items
          .filter((i) => i.tratamento === t)
          .sort((a, b) => (a.saiu ? 1 : 0) - (b.saiu ? 1 : 0));
      const list = [
        {
          key: "manual",
          icon: "mdi-account-alert-outline",
          iconColor: "#b06a12",
          title: "Correção manual",
          subtitle:
            "O agente não resolve sozinho — alguém precisa ajustar no Phoenix",
          items: by("manual"),
          empty: "Nenhuma pendência precisando de correção manual. 🎉",
        },
        {
          key: "agente",
          icon: "mdi-robot-outline",
          iconColor: "#2f7fd1",
          title: "O agente vai resolver",
          subtitle:
            "O Resolver tenta a correção automática na próxima execução",
          items: by("agente"),
          empty: "Nenhuma pendência na fila do agente.",
        },
      ];
      list.push({
        key: "resolvida",
        icon: "mdi-check-circle-outline",
        iconColor: "#0f7a6e",
        title: "Resolvidas pelo agente",
        subtitle: "Correção aplicada e pendência fechada no período",
        items: by("resolvida"),
        empty: "Nenhuma pendência resolvida pelo agente neste período.",
      });
      list.push({
        key: "resolvida_reimport",
        icon: "mdi-file-sync-outline",
        iconColor: "#1f7a7c",
        title: "Resolvidas via reimportação",
        subtitle:
          "A CVC corrigiu direto no portal deles — o agente não precisou agir",
        items: by("resolvida_reimport"),
        empty: "Nenhuma pendência resolvida via reimportação neste período.",
      });
      return list;
    },
  },
  watch: {
    range() {
      this.load();
    },
    agent() {
      this.load();
    },
    base() {
      this.load();
    },
  },
  created() {
    this.load();
  },
  methods: {
    grupoAberto(key) {
      return !!this.gruposAbertos[key];
    },
    toggleGrupo(key) {
      this.$set(this.gruposAbertos, key, !this.gruposAbertos[key]);
    },
    async load() {
      this.loading = true;
      this.openId = null;
      try {
        const data = await getPendenciesDetail(
          this.range,
          this.agent,
          this.base
        );
        this.totals = data.totals;
        this.items = data.items;
        this.saidas = data.saidas || EMPTY_SAIDAS;
      } catch (err) {
        console.error("Falha ao carregar detalhamento de pendências", err);
        this.totals = EMPTY_TOTALS;
        this.items = [];
        this.saidas = EMPTY_SAIDAS;
      } finally {
        this.loading = false;
      }
    },
    // Linhas da dica ao passar o mouse no chip de e-mail: para quem foi, cópias, filial e quando.
    emailTooltip(email) {
      const dest = email.destinatarios;
      const filial = email.filial ? `Filial ${email.filial}` : null;
      if (email.status === "enviado") {
        if (!dest) {
          return [
            "Destino não registrado",
            "(e-mail enviado antes de 30/09)",
            ...(email.quando ? [`Enviado em ${email.quando}`] : []),
          ];
        }
        return [
          `Para: ${dest.to}`,
          ...(dest.cc.length ? [`Cópia: ${dest.cc.join(", ")}`] : []),
          ...(filial ? [filial] : []),
          ...(email.quando ? [`Enviado em ${email.quando}`] : []),
        ];
      }
      if (email.status === "falhou") {
        return [
          `Falha ao enviar${email.motivo ? `: ${email.motivo}` : ""}`,
          ...(dest ? [`Destino seria: ${dest.to}`] : []),
          ...(filial ? [filial] : []),
        ];
      }
      if (email.status === "nao_enviado") {
        return [`Não enviado${email.motivo ? ` — ${email.motivo}` : ""}`];
      }
      return ["Sem registro de e-mail para este voucher"];
    },
    emailLabel(st) {
      return (
        {
          enviado: "Enviado",
          nao_enviado: "Não enviado",
          falhou: "Falhou",
          sem_registro: "Sem registro",
        }[st] || st
      );
    },
    tratamentoLabel(t) {
      return TRATAMENTO_LABEL[t] || t;
    },
    // Cabecalho de cada lista: quantas RESERVAS e quantas PENDENCIAS (uma reserva pode ter varias).
    // Listas abertas (correcao manual / fila do agente) contam o que ainda falta resolver; as de resolvidas, o que foi resolvido.
    contagemGrupo(group) {
      const aberta = group.key === "manual" || group.key === "agente";
      const reservas = group.items.length;
      const campo = aberta ? "pendRestantes" : "pendResolvidas";
      const pendencias = group.items.reduce(
        (s, i) => s + (Number(i[campo]) || 0),
        0
      );
      const rReserva = aberta
        ? reservas === 1
          ? "reserva"
          : "reservas"
        : reservas === 1
        ? "reserva resolvida"
        : "reservas resolvidas";
      // grupos de origem: as pendencias contadas sao as que AINDA estao em aberto
      const sufixo = aberta ? " em aberto" : "";
      return `${reservas} ${rReserva} · ${pendencias} ${
        pendencias === 1 ? "pendência" : "pendências"
      }${sufixo}`;
    },
    toggle(id) {
      this.openId = this.openId === id ? null : id;
    },
    pct(n) {
      if (!this.totalBarra) return "0%";
      return `${(n / this.totalBarra) * 100}%`;
    },
    segLabelVisible(n) {
      return this.totalBarra && n / this.totalBarra > 0.06;
    },
  },
};
</script>

<style src="./styles.scss" scoped lang="scss" />
